import axios from 'axios'

export const ERR_MSG_STD = 'Erro inesperado ao processar a requisição.'
export const ERR_MSG_COMM = 'Erro na comunicação com o servidor.'
export const ERR_MSG_TIMEOUT = 'Tempo limite excedido. Tente novamente.'

/**
 * Quando `true`, mensagens de resposta 5xx não são exibidas ao usuário — o
 * Lumen costuma devolver texto de exceção com detalhe de infraestrutura em 500.
 *
 * Default `false`: preserva a mensagem do servidor, que é o comportamento
 * pretendido pelo `handleAxiosErrorUtil` original. Trocar para `true` é uma
 * decisão de produto, não de infraestrutura.
 */
export const MASK_SERVER_MESSAGE_ON_5XX = false

/** Marca de identidade resistente a duplicação de módulo no bundle. */
const API_ERROR_BRAND = '__isApiError'

export interface ApiErrorInit {
  message: string
  path?: string
  status?: number | null
  code?: string | null
  data?: unknown
}

/**
 * Erro padronizado da camada de serviço.
 *
 * Estende `Error` — ao contrário do `throw { ... }` do V1 — para que:
 * - `instanceof Error` funcione em toda a aplicação;
 * - exista stack trace (o V1 aparece como "Non-Error exception" no Sentry);
 * - o Vue NÃO transforme o objeto em proxy reativo (`toRawType` de um Error
 *   é `Error`, tipo que o `reactive()` ignora).
 */
export class ApiError extends Error {
  readonly path: string
  readonly status: number | null
  readonly code: string | null
  readonly data: unknown

  constructor(init: ApiErrorInit) {
    super(init.message || ERR_MSG_STD)

    this.name = 'ApiError'
    this.path = init.path ?? ''
    this.status = init.status ?? null
    this.code = init.code ?? null
    this.data = init.data ?? null

    // Necessário quando o target do TS é ES5, senão `instanceof` quebra.
    Object.setPrototypeOf(this, ApiError.prototype)

    Object.defineProperty(this, API_ERROR_BRAND, {
      value: true,
      enumerable: false,
      writable: false,
    })
  }

  /** Cópia independente — usada por clones de estado. */
  clone(): ApiError {
    return new ApiError({
      message: this.message,
      path: this.path,
      status: this.status,
      code: this.code,
      data: safeCloneData(this.data),
    })
  }
}

export function isApiError(value: unknown): value is ApiError {
  if (value instanceof ApiError) return true

  return Boolean(
    value &&
    typeof value === 'object' &&
    (value as Record<string, unknown>)[API_ERROR_BRAND] === true,
  )
}

function safeCloneData(data: unknown): unknown {
  if (data === null || typeof data !== 'object') return data

  try {
    return structuredClone(data)
  } catch {
    return data
  }
}

/**
 * Formato lançado pelo `handleAxiosErrorUtil` (V1).
 *
 * `ApiService.get()` já aplica esse tratamento internamente e NÃO pode ser
 * alterado — então todo GET chega aqui com essa forma, nunca como AxiosError.
 */
interface LegacyApiError {
  path?: unknown
  status?: unknown
  data?: unknown
  message?: unknown
}

function isLegacyApiError(value: unknown): value is LegacyApiError {
  if (!value || typeof value !== 'object') return false

  const candidate = value as LegacyApiError

  return typeof candidate.message === 'string' && ('path' in candidate || 'status' in candidate)
}

/**
 * Extrai a mensagem mais útil do corpo de erro da API.
 *
 * Cobre `{ message }`, `{ error }` e o formato de validação do Lumen
 * `{ errors: { campo: ["msg"] } }`, que o V1 descartava.
 */
export function readServerMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null

  const payload = data as { message?: unknown; error?: unknown; errors?: unknown }

  if (typeof payload.message === 'string' && payload.message.length > 0) return payload.message
  if (typeof payload.error === 'string' && payload.error.length > 0) return payload.error

  if (payload.errors && typeof payload.errors === 'object') {
    const first = Object.values(payload.errors as Record<string, unknown>)[0]

    if (Array.isArray(first) && typeof first[0] === 'string') return first[0]
    if (typeof first === 'string') return first
  }

  return null
}

function resolveMessage(status: number | null, serverMessage: string | null, fallback: string): string {
  if (MASK_SERVER_MESSAGE_ON_5XX && status !== null && status >= 500) return ERR_MSG_COMM

  return serverMessage ?? fallback
}

/**
 * Converte qualquer erro em `ApiError`.
 *
 * **Idempotente**: receber um `ApiError` devolve o mesmo objeto. É o conserto
 * central do V1, onde `ApiService.get()` normalizava, o
 * `handleFetchServiceUtil` normalizava de novo, `axios.isAxiosError()` dava
 * `false` na segunda passagem e `status`, `data` e a mensagem real eram
 * substituídos por "Erro inesperado ao processar a requisição.".
 */
export function normalizeApiError(error: unknown, resource = ''): ApiError {
  // 1. Já normalizado — repassa intacto.
  if (isApiError(error)) return error

  // 2. AxiosError cru — caminho de post/put/deleteV2, que não têm catch interno.
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? null
    const server = readServerMessage(error.response?.data)

    if (error.code === 'ECONNABORTED') {
      return new ApiError({ message: ERR_MSG_TIMEOUT, path: resource, status, code: 'TIMEOUT' })
    }

    return new ApiError({
      message: resolveMessage(status, server, error.message || ERR_MSG_COMM),
      path: resource,
      status,
      code: error.code ?? null,
      data: error.response?.data ?? null,
    })
  }

  // 3. Objeto plano do handleAxiosErrorUtil — caminho de todo GET.
  if (isLegacyApiError(error)) {
    const status = typeof error.status === 'number' ? error.status : null
    const server = readServerMessage(error.data)
    const legacy = typeof error.message === 'string' ? error.message : ERR_MSG_COMM

    return new ApiError({
      message: resolveMessage(status, server, legacy),
      path: typeof error.path === 'string' && error.path.length > 0 ? error.path : resource,
      status,
      data: error.data ?? null,
    })
  }

  // 4. Error nativo — inclui falhas do valideTokenTime(), que ocorrem antes
  //    do catch interno do ApiService e por isso sobem sem tratamento.
  if (error instanceof Error) {
    return new ApiError({ message: error.message || ERR_MSG_STD, path: resource })
  }

  if (typeof error === 'string' && error.length > 0) {
    return new ApiError({ message: error, path: resource })
  }

  return new ApiError({ message: ERR_MSG_STD, path: resource })
}

/** Paridade com `handleAxiosErrorUtil`, agora lançando `ApiError`. */
export function handleAxiosErrorUtilV2(error: unknown, resource: string): never {
  throw normalizeApiError(error, resource)
}
